import { Head, Link, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import TiktokConnectCta from "@/components/social-accounts/tiktok-connect-cta"
import TiktokOAuthStartForm from "@/components/social-accounts/tiktok-oauth-start-form"
import { Alert, AlertDescription } from "@/components/ui/alert"
import type { WizardStepKey } from "@/components/wizard/wizard-progress"
import { WizardShell } from "@/components/wizard/wizard-shell"
import { useOnline } from "@/hooks/use-online"
import { creatorMissionsPath, startOnboardingTiktokPath } from "@/routes"
import type { SharedProps } from "@/types"

export interface WizardTiktokBrand {
  id?: number
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

/**
 * TikTok step error enum. `identity_mismatch` is DROPPED here per FE TRD
 * v2 — first-time mismatches route to the dedicated `IdentityMismatch`
 * page (Flow E'). The remaining 4 reasons render same-screen with
 * retry per OKS-23 Spec §3.
 *
 * **OKS-311** — `"start_failed"` added: server-side rescue path in
 * `Onboarding::TiktokController#create` sets `flash[:tiktok_oauth_error]
 * = "start_failed"` when the OAuth round-trip can't even leave the
 * Crew origin (missing `TIKTOK_CLIENT_KEY` / `APP_HOST`, OAuth URL
 * build failure, PKCE failure). One generic variant per
 * DECISIONS-OKS-311 lock #1 (avoid leaking internals).
 */
export type WizardTiktokError =
  | null
  | "oauth_denied"
  | "already_linked"
  | "network_error"
  | "state_expired"
  | "start_failed"
  // OKS-764 — the verified handle was rejected by validation (out of the
  // shared `{2,24}` domain) during `#complete`. Impossible for a real
  // TikTok handle, but surfaced as a handled retry state rather than a 500.
  | "verify_failed"

const KNOWN_ERROR_KEYS = new Set<string>([
  "oauth_denied",
  "already_linked",
  "network_error",
  "state_expired",
  "start_failed",
  "verify_failed",
])

export interface WizardTiktokProps {
  brand: WizardTiktokBrand
  /** Server-computed required steps for the active brand. 3 dots on the
   *  first-brand happy path. */
  required_steps: readonly WizardStepKey[]
  /** Server-resolved POST URL into the OAuth start endpoint. FE never
   *  reconstructs it; the helper is the safe fallback. */
  oauth_start_url: string
  /**
   * Same-screen error variant. Falls into the OKS-23 inline alert when
   * non-null. `identity_mismatch` is handled at the dedicated page.
   */
  error: WizardTiktokError
  support_email: string
}

/**
 * Wizard Step 1 — TikTok OAuth start.
 *
 * Wraps `<WizardShell>` with the OKS-23 partner-styled `<TiktokConnectCta>`
 * inside a top-level `<TiktokOAuthStartForm>` (NOT Inertia — cross-origin
 * 302 to `tiktok.com` requires top-level browser navigation). On the
 * second-brand path this page is server-skipped — the dispatcher 303s
 * past it; if a creator hits the URL directly anyway they see the same
 * UI but the BE controller handles the no-op.
 *
 * Error handling: 4 same-screen variants per OKS-23 (oauth_denied /
 * already_linked / network_error / state_expired). `identity_mismatch`
 * routes to the dedicated `IdentityMismatch` dead-end page (Flow E').
 */
export default function Tiktok({
  brand,
  required_steps,
  oauth_start_url,
  error,
  support_email,
}: WizardTiktokProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const headingRef = useRef<HTMLHeadingElement>(null)

  // OKS-311 — local CTA loading state. The form is a plain HTML
  // `<form>` (cross-origin OAuth nav, NOT Inertia XHR), so we can't
  // hook into Inertia's `processing` render prop. Flipping this on
  // `onSubmit` re-renders the CTA with its spinner before the
  // browser begins the native cross-origin POST. On a BE rescue
  // (303 back to `/onboarding/tiktok`) the page re-mounts fresh and
  // the state resets to `false` — no manual cleanup needed.
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // Page-level errors from a redirect-back (e.g. invalid OAuth state).
  // Same shape as OKS-22's account-step pattern.
  const pageProps = usePage<
    Partial<SharedProps> & { errors?: Record<string, string[]> }
  >().props
  const baseErrors = pageProps.errors?.base

  // OKS-376 — when TikTok association is optional, the connect screen is
  // escapable: it gains optional framing + a "Maybe later" link back to
  // brand home, so an OAuth error (incl. the rejected-app `start_failed`)
  // is never a dead-end. Strict mode (flag OFF) keeps it a hard step 1.
  const tiktokOptional = pageProps.tiktok_association_optional === true

  const submitAction = oauth_start_url ?? startOnboardingTiktokPath()

  return (
    <WizardShell brand={brand} step="tiktok" required_steps={required_steps}>
      <Head>
        <title>{t("wizard.tiktok.head_title")}</title>
      </Head>
      <div data-testid="wizard-step-tiktok" className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1
            ref={headingRef}
            tabIndex={-1}
            data-testid="wizard-step-tiktok-heading"
            className="text-2xl font-bold tracking-tight outline-none"
          >
            {t("wizard.tiktok.heading")}
          </h1>
        </div>
        <p className="text-sm" data-testid="wizard-step-tiktok-explainer">
          {t("wizard.tiktok.explainer", { brand: brand.name })}
        </p>

        {tiktokOptional && (
          <p
            className="text-muted-foreground text-sm"
            data-testid="wizard-step-tiktok-optional-subtitle"
          >
            {t("tiktok_optional.tiktok.optional_subtitle")}
          </p>
        )}

        {/*
          OAuth same-screen error surface — 4 variants per OKS-23 Spec §3.
          `identity_mismatch` is DROPPED from this enum (FE TRD v2): the
          dedicated `pages/Onboarding/IdentityMismatch.tsx` dead-end owns
          that flow now. Defensive guard via the explicit allowlist:
          even if a future server drift smuggles an off-enum error
          through, we render NOTHING (better than mis-classifying it).
        */}
        {/*
          OKS-311 — per-variant translation key with `defaultValue`
          fallback to the legacy `signup.errors.base` copy. New
          `start_failed` variant renders the PO-locked FR copy
          (`signup.errors.start_failed`) interpolating `{{support_email}}`
          from the server prop. Pre-existing variants
          (oauth_denied / already_linked / network_error / state_expired)
          have no per-variant key yet → they keep falling back to
          `signup.errors.base` exactly as before.
        */}
        {error && KNOWN_ERROR_KEYS.has(error) && (
          <Alert
            variant="destructive"
            data-testid={`wizard-step-tiktok-error-${error}`}
            role="alert"
          >
            <AlertDescription>
              {t(`signup.errors.${error}`, {
                support_email,
                defaultValue: t("signup.errors.base"),
              })}
            </AlertDescription>
          </Alert>
        )}
        {Array.isArray(baseErrors) && baseErrors.length > 0 && (
          <Alert
            variant="destructive"
            data-testid="wizard-step-tiktok-error-base"
          >
            <AlertDescription>{baseErrors.join(", ")}</AlertDescription>
          </Alert>
        )}

        {!online && (
          <p
            className="text-muted-foreground text-sm"
            role="status"
            aria-live="polite"
            data-testid="wizard-step-tiktok-offline"
          >
            {t("signup.errors.offline_helper")}
          </p>
        )}

        <div className="bg-background sticky bottom-0 -mx-6 mt-2 flex flex-col gap-3 border-t px-6 py-4 sm:static sm:mx-0 sm:p-0">
          <TiktokOAuthStartForm
            action={submitAction}
            data-testid="wizard-step-tiktok-form"
            className="contents"
            onSubmit={() => setSubmitting(true)}
          >
            <TiktokConnectCta
              disabled={!online}
              processing={submitting}
              data-testid="tiktok-connect-cta"
            />
          </TiktokOAuthStartForm>

          {tiktokOptional && (
            <Link
              href={creatorMissionsPath()}
              className="text-muted-foreground hover:text-foreground text-center text-sm underline-offset-4 hover:underline"
              data-testid="wizard-step-tiktok-maybe-later"
            >
              {t("tiktok_optional.tiktok.maybe_later")}
            </Link>
          )}
        </div>
      </div>
    </WizardShell>
  )
}
